import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Scale, Download, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_VALUE = '__ALL__';

const LegalReport = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    settlement_method: '',
    went_to_default: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.settlement_method) params.settlement_method = filters.settlement_method;
      if (filters.went_to_default) params.went_to_default = filters.went_to_default;
      const res = await axios.get(`${API}/reports/legal`, {
        headers: getAuthHeaders(),
        params,
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportCSV = () => {
    if (!data) return;
    const header = [
      'Deal #',
      'Merchant',
      'Gross Collected',
      'Via Lawyer',
      'In-House',
      'Lawyer Fees',
      'Net Recovery',
      'Settlement Method',
      'Went to Default',
      'Legal Fees (Settlement)',
      'Settlement Amount',
      'Written Off',
      'Settlement Date',
    ];
    const rows = data.rows.map((r) => [
      r.deal_id_number || '',
      (r.merchant_name || '').replace(/,/g, ' '),
      r.gross_collected,
      r.lawyer_collected ?? '',
      r.in_house_collected ?? '',
      r.lawyer_fees_collections,
      r.net_recovery,
      r.settlement_method || '',
      r.went_to_default == null ? '' : r.went_to_default ? 'Yes' : 'No',
      r.legal_fees_settlement,
      r.settlement_amount ?? '',
      r.settlement_written_off ?? '',
      r.settlement_date || '',
    ]);
    const csv = [header, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legal-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="legal-report">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
              data-testid="legal-from-date"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
              data-testid="legal-to-date"
            />
          </div>
          <div>
            <Label className="text-xs">Settlement Method</Label>
            <Select
              value={filters.settlement_method || ALL_VALUE}
              onValueChange={(v) => setFilters((f) => ({ ...f, settlement_method: v === ALL_VALUE ? '' : v }))}
            >
              <SelectTrigger data-testid="legal-method-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All</SelectItem>
                <SelectItem value="Attorney/Legal">Attorney/Legal</SelectItem>
                <SelectItem value="In-House">In-House</SelectItem>
                <SelectItem value="Broker/ISO">Broker/ISO</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Went to Default?</Label>
            <Select
              value={filters.went_to_default || ALL_VALUE}
              onValueChange={(v) => setFilters((f) => ({ ...f, went_to_default: v === ALL_VALUE ? '' : v }))}
            >
              <SelectTrigger data-testid="legal-default-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All</SelectItem>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
        {[
          { label: 'Gross Recovered', val: totals.total_gross_collections, color: 'text-emerald-700' },
          { label: 'Via Lawyer', val: totals.total_lawyer_collections, color: 'text-emerald-700' },
          { label: 'In-House', val: totals.total_in_house_collections, color: 'text-blue-700' },
          { label: 'Lawyer Fees Paid', val: totals.total_lawyer_fees_collections, color: 'text-red-700' },
          { label: 'Net Recovery', val: totals.total_net_recovery, color: 'text-emerald-800' },
          { label: 'Settlement Legal Fees', val: totals.total_legal_fees_settlement, color: 'text-purple-700' },
          { label: 'Total Written Off (Settled)', val: totals.total_written_off, color: 'text-amber-700' },
          // Josh 10/01: PFS's own share of legal activity — what a PFS statement would have said
          { label: 'PFS Retained (own positions)', val: data?.pfs_retained?.net, color: 'text-indigo-700' },
        ].map((s) => (
          <Card key={s.label} className="bg-white">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{s.label}</div>
              <div className={`text-2xl font-bold ${s.color} tabular-nums mt-1`}>
                {formatCurrency(s.val || 0)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-deal table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-slate-600" />
            <CardTitle className="text-base">
              Legal Activity by Deal ({data?.rows?.length || 0})
            </CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              data-testid="legal-refresh-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={exportCSV}
              data-testid="legal-export-csv"
              className="bg-slate-700 hover:bg-slate-800 text-white"
            >
              <Download className="h-3 w-3 mr-1" /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Loading…</div>
          ) : !data?.rows?.length ? (
            <div className="py-12 text-center text-sm text-gray-500">
              No legal activity in the selected window.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left">Deal</th>
                    <th className="px-4 py-2 text-left">Merchant</th>
                    <th className="px-4 py-2 text-right">Gross</th>
                    <th className="px-4 py-2 text-right">Via Lawyer</th>
                    <th className="px-4 py-2 text-right">In-House</th>
                    <th className="px-4 py-2 text-right">Lawyer Fees</th>
                    <th className="px-4 py-2 text-right">Net Recovery</th>
                    <th className="px-4 py-2 text-left">Method</th>
                    <th className="px-4 py-2 text-right">Legal Fees (Settlement)</th>
                    <th className="px-4 py-2 text-right">Written Off</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.rows.map((r) => (
                    <tr key={r.deal_id} data-testid={`legal-row-${r.deal_id_number}`}>
                      <td className="px-4 py-2 font-mono text-xs">
                        <Link
                          to={`/deals/${r.deal_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          #{r.deal_id_number || '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-2 truncate max-w-[200px]">
                        {r.merchant_name || '—'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-emerald-700">
                        {formatCurrency(r.gross_collected)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {formatCurrency(r.lawyer_collected || 0)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-blue-700">
                        {formatCurrency(r.in_house_collected || 0)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-red-600">
                        {formatCurrency(r.lawyer_fees_collections)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {formatCurrency(r.net_recovery)}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {r.settlement_method || <span className="text-gray-400">—</span>}
                        {r.went_to_default && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-purple-700">
                        {r.legal_fees_settlement > 0 ? formatCurrency(r.legal_fees_settlement) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-amber-700">
                        {r.settlement_written_off ? formatCurrency(r.settlement_written_off) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalReport;
